import { EquipLightPlayer, ItemIcons, loadCustomRodelas, nimbusLights, type CustomRodelas, SHOW_HEIGHT, SHOW_WIDTH, showLayers, WingShowPlayer, wingPicOf, wingShowPlacement, type IconTemplate, type Sex, type WornPiece } from '@ddt/game-engine';
import { assetCatalog } from './audio';

let icons: Promise<ItemIcons | null> | null = null;

/** O resolvedor de ícones do app: nasce do manifest de assets (baixado uma vez) e guarda cada ícone já montado. */
function resolver(): Promise<ItemIcons | null> {
  if (!icons) {
    icons = assetCatalog()
      .then((catalog) => new ItemIcons(catalog))
      .catch(() => {
        icons = null; // sem manifest agora: a próxima tela tenta de novo
        return null;
      });
  }
  return icons;
}

/** URL da imagem do item, ou `null` quando a arte não existe (ou o manifest não carregou). */
export async function itemIconUrl(template: IconTemplate | null | undefined): Promise<string | null> {
  if (!template) return null;
  return (await resolver())?.url(template) ?? null;
}

/** Imagem do boneco em pé com as peças equipadas (quadros `show`), ou `null` se a arte não carregou. */
export async function characterShowUrl(sex: Sex, worn: readonly WornPiece[]): Promise<string | null> {
  const [catalog, icons] = await Promise.all([assetCatalog().catch(() => null), resolver()]);
  if (!catalog || !icons) return null;
  return icons.composite(showLayers(sex, worn, (atlas) => catalog.has(atlas)), { w: SHOW_WIDTH, h: SHOW_HEIGHT });
}

/** A asa do boneco em pé: a imagem (`blob:`) e onde ela cai no quadro do corpo de `SHOW_WIDTH` × `SHOW_HEIGHT` px, já ampliada (pode passar da borda). */
export interface WingImage {
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Asa equipada do boneco em pé, ou `null` sem asa, sem arte ou se a arte não carregou. */
export async function characterWing(worn: readonly WornPiece[]): Promise<WingImage | null> {
  const [catalog, icons] = await Promise.all([assetCatalog().catch(() => null), resolver()]);
  if (!catalog || !icons) return null;
  const placement = wingShowPlacement(worn, (atlas) => catalog.has(atlas));
  const image = placement ? await icons.frameImage(placement.atlas, placement.frame) : null;
  if (!placement || !image) return null;
  return { url: image.url, x: placement.x, y: placement.y, width: image.width * placement.scale, height: image.height * placement.scale };
}

/** Asa animada do boneco em pé (o bater de asas do legado), ou `null` sem asa, sem arte ou sem animação conhecida: nesse caso vale `characterWing`. Cada boneco recebe uma tela própria. */
export async function characterWingPlayer(worn: readonly WornPiece[]): Promise<WingShowPlayer | null> {
  const catalog = await assetCatalog().catch(() => null);
  if (!catalog) return null;
  const pic = wingPicOf(worn, (atlas) => catalog.has(atlas));
  if (!pic) return null;
  return WingShowPlayer.load(catalog, pic).catch(() => null);
}

/**
 * As luzes do boneco em pé para o brilho `nimbus` (aura da arma e luz nos pés, ADR 0029), cada uma com tela própria. Luz sem arte no manifest fica de fora:
 * o boneco aparece sem ela.
 */
export async function characterLights(nimbus: number): Promise<EquipLightPlayer[]> {
  if (!Number.isInteger(nimbus) || nimbus <= 0) return [];
  const catalog = await assetCatalog().catch(() => null);
  if (!catalog) return [];
  // O índice das rodelas do painel só é lido quando a arma acende uma rodela fora das três do legado.
  const custom = nimbus % 100 > 3 ? await customRodelas(catalog.baseUrl) : new Map();
  const refs = nimbusLights(nimbus, custom);
  if (refs.length === 0) return [];
  const players = await Promise.all(refs.map((ref) => EquipLightPlayer.load(catalog, ref, custom).catch(() => null)));
  return players.filter((p): p is EquipLightPlayer => p !== null);
}

const RODELAS_TTL_MS = 60_000;
let rodelas: { at: number; value: Promise<CustomRodelas> } | null = null;

/** O índice das rodelas criadas no painel, relido no máximo uma vez por minuto (uma rodela nova aparece sem recarregar a página por muito tempo). */
function customRodelas(baseUrl: string): Promise<CustomRodelas> {
  if (!rodelas || Date.now() - rodelas.at > RODELAS_TTL_MS) rodelas = { at: Date.now(), value: loadCustomRodelas(baseUrl) };
  return rodelas.value;
}

/** Uma camada do boneco já desenhada em imagem própria. `depth` diz o quanto ela acompanha o olhar: 1 move por inteiro, 0 fica parada. */
export interface ShowLayerImage {
  url: string;
  depth: number;
}

/** Rosto, olhos e óculos acompanham o olhar por inteiro; cabelo e chapéu, só um pouco; roupa e terno ficam parados. */
function gazeDepth(atlas: string): number {
  const folder = /\/equip\/[mf]\/([a-z]+)\//.exec(atlas)?.[1];
  if (folder === 'face' || folder === 'eff' || folder === 'glass') return 1;
  if (folder === 'hair' || folder === 'head') return 0.4;
  return 0;
}

/** Como `characterShowUrl`, mas uma imagem por camada, de trás para a frente, para a tela poder mover cada uma. `null` se a arte não carregou. */
export async function characterShowLayers(sex: Sex, worn: readonly WornPiece[]): Promise<ShowLayerImage[] | null> {
  const [catalog, icons] = await Promise.all([assetCatalog().catch(() => null), resolver()]);
  if (!catalog || !icons) return null;
  const layers = showLayers(sex, worn, (atlas) => catalog.has(atlas));
  const urls = await Promise.all(layers.map((layer) => icons.composite([layer], { w: SHOW_WIDTH, h: SHOW_HEIGHT })));
  const images = layers.flatMap((layer, i) => (urls[i] ? [{ url: urls[i]!, depth: gazeDepth(layer.atlas) }] : []));
  return images.length > 0 ? images : null;
}
